import { registerComponents } from './register-components'
import { ChartManager, PieChartService, TableChartService } from "../charts/index.js";
import { ChartService } from '../charts/services/chart.service.js';

import '../newsletters/newsletter'

// Global Styles
import '../core/styles/webflow.scss'
import '../core/styles/weglot.scss'
import { AuthService } from '../auth/services/auth.service.js';
import { OTPService } from '../auth/services/otp.service.js';
import { RouteGuardian } from '../routing/guardian.service.js';
import { NewsService } from '../news/news.service.js';
import { AssetScreenerService } from '../assetScreener/services/assetScreener.service.js';
import { TokenDataService } from '../tokenData/services/tokenData.service.js';
import { PermissionService } from './services/permission-service.js';
import { BubbleChartService } from '../charts/services/bubble.service.js';
import { NumberService } from '../numbers/numbers.service.js';
import axios from 'axios';
import { AuthInterceptor } from './adapters/interceptors/auth.interceptor.js';
import { NewsletterService } from '../newsletter/newsletter.service.js';
import { OldNewsletterService } from '../newsletters/services/newsletter.service.js';
import { ProfileService } from '../profile/profile.service.js';
import { WeglotService } from '../internationalize/weglot.service.js';

interface TbwServices {
  authService: AuthService;
  newsService: NewsService;
  assetScreenerService: AssetScreenerService;
  tokenDataService: TokenDataService;
  chartService: ChartService;
  permissionService: PermissionService;
  bubbleChartService: BubbleChartService;
  pieChartService: PieChartService;
  numberService: NumberService;
  tableChartService: TableChartService;
  newsletterService: NewsletterService;
  oldNewsletterService: OldNewsletterService;
  profileService: ProfileService;
  weglotService: WeglotService;
}

// Extend the Window interface to include authService
declare global {
  interface Window {
    tbwServices: TbwServices;
  }
}

// TODO: minimal compute for guardian actions
const init = async () => {
  const getConfig = async (env: ImportMetaEnv) => Promise.resolve({
    endpoints: {
      api: env.VITE_API_ENDPOINT,
      charts: {
        v2: env.VITE_CHART_API_ENDPOINT,
        v1: env.VITE_CHART_API_FAILOVER_ENDPOINT
      },
      search: {
        apiKey: env.VITE_MEILI_CLIENT_KEY,
        url: env.VITE_MEILI_ENDPOINT,
      },
      marketing: env.VITE_MARKETING_WEBSITE,
      weglot: {
        apiKey: env.VITE_WEGLOT_API_KEY
      }
    }
  });

  const config = await getConfig(import.meta.env);
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }

  const authAdapter = axios.create({
    baseURL: config.endpoints.api + 'sessions',
    headers,
  });
  const otpAdapter = axios.create({
    baseURL: config.endpoints.api + 'sessions',
    headers,
  });
  const chartAdapter = axios.create({
    baseURL: config.endpoints.charts.v2 + 'graph',
    headers
  });
  const numberAdapter = axios.create({
    baseURL: config.endpoints.charts.v2 + 'widgets',
    headers
  });
  const chartAdapterFailOver = axios.create({
    baseURL: config.endpoints.charts.v1 + 'graph',
    headers
  });
  const newsletterAdapter = axios.create({
    baseURL: config.endpoints.api + 'newsletter',
    headers
  });
  const oldNewsletterAdapter = axios.create({
    baseURL: config.endpoints.api + 'newsletter',
    headers
  });
  const profileAdapter = axios.create({
    baseURL: config.endpoints.api + 'profile',
    headers
  });
  const newsAdapter = axios.create({
    baseURL: config.endpoints.charts.v2 + 'articles',
    headers
  });
  const contentAdapter = axios.create({
    baseURL: config.endpoints.api,
    headers
  });

  const otpService = new OTPService(otpAdapter);
  const authService = new AuthService(authAdapter, otpService);
  const authInterceptor = new AuthInterceptor(authService);

  // Add adapters that need AUTH
  [authAdapter, otpAdapter, chartAdapter, chartAdapterFailOver, newsAdapter, contentAdapter, oldNewsletterAdapter, profileAdapter].forEach(adapter => {
    authInterceptor.setupInterceptors(adapter);
  });

  const newsService = new NewsService(newsAdapter);
  const assetScreenerService = new AssetScreenerService(contentAdapter);
  const tokenDataService = new TokenDataService(contentAdapter);
  const permissionService = new PermissionService(authService);

  const chartService = new ChartService(chartAdapter, chartAdapterFailOver);
  const bubbleChartService = new BubbleChartService(chartService);
  const pieChartService = new PieChartService(chartService);
  const tableChartService = new TableChartService(chartService)
  const numberService = new NumberService(numberAdapter);
  const newsletterService = new NewsletterService(newsletterAdapter);
  const oldNewsletterService = new OldNewsletterService(oldNewsletterAdapter, authService);
  const profileService = new ProfileService(profileAdapter);

  const weglotService = new WeglotService(config.endpoints.weglot.apiKey);

  // required for components services injection
  window.tbwServices = {
    authService,
    newsService,
    assetScreenerService,
    tokenDataService,
    chartService,
    permissionService,
    bubbleChartService,
    pieChartService,
    numberService,
    tableChartService,
    newsletterService,
    oldNewsletterService,
    profileService,
    weglotService
  };

  const routeGuardian = new RouteGuardian(authService);
  const canEnterRoute = await routeGuardian.onRouteEntered()
  if (!canEnterRoute) { return }

  registerComponents();
  const chartManager = new ChartManager(chartService);
  chartManager.init();
  return true;
}

init().then(_r => console.log("App initialized"));